import axios from 'axios';
import { setAlert } from './alert.js';
import {
    REGISTER_SUCCESS,
    REGISTER_FAIL,
    USER_LOADED,
    AUTH_ERROR,
    AUTH_LOAD_START,
    AUTH_LOAD_ERROR,
    LOGIN_SUCCESS,
    LOGIN_FAIL,
    LOGOUT,
    CLEAR_PROFILE,
    USER_UPDATED,
    AUTH_UPDATE_FAIL,
    AUTH_UPDATE,
} from './types';

import setAuthToken from '../utils/setAuthToken';
import {requestGet, requestPost} from "../utils/request";
import {AUTH_USER} from "../config/requestEndpoints";
import {UserSettings} from "../components/Atoms/client";
import {
    clearAuthorization,
    permissionsMapFromCapabilities,
    setAuthorization
} from "../utils/capabilityAccess";

const applyAuthorizationPayload = (data) => {
    if (data?.authorization) {
        setAuthorization(data.authorization);
        // In-page button checks derive from OrgRole capabilities (no RolePermission).
        const permissions = permissionsMapFromCapabilities(
            data.authorization.capabilities
        );
        UserSettings.setUserPermissions({
            permissions,
            roleName: data.authorization.orgRoleName || ''
        });
    }
};

// Load user — refreshes OrgRole capabilities; EnhancedDashboard rebinds nav via redux.
export const loadUser = () => async dispatch => {
    if (localStorage.token) {
        setAuthToken(localStorage.token);
    }

    dispatch({ type: AUTH_LOAD_START });

    try {
        // Cache-bust: webpack proxy / browser 304 on /api/auth was overwriting
        // a good login authorization with a stale empty-capabilities body.
        const uri = `${AUTH_USER}?_=${Date.now()}`;
        const {data, success, message} = await requestGet({
            uri,
            showTriggers: true,
            headers: {
                'Cache-Control': 'no-cache',
                Pragma: 'no-cache'
            }
        });
        if (!success || !data?.authorization) {
            dispatch({
                type: AUTH_LOAD_ERROR,
                payload: message || data?.message || 'Failed to load user'
            });
            return;
        }
        applyAuthorizationPayload(data);
        dispatch({
            type: USER_LOADED,
            payload: data
        });
    } catch (err) {
        // Keep token — ERROR state shows Retry (do not treat as logout).
        dispatch({
            type: AUTH_LOAD_ERROR,
            payload: err?.message || 'Failed to load user'
        });
    }
};

// Register user
export const register = ({ name, email, password }) => async dispatch => {
    const config = {
        headers: {
            'Content-Type': 'application/json'
        }
    };

    const body = JSON.stringify({ name, email, password });

    try {
        const res = await axios.post('/api/users', body, config);
        dispatch({
            type: REGISTER_SUCCESS,
            payload: res.data
        });

        dispatch(loadUser());
    } catch (err) {
        const errors = err.response.data.errors;

        if (errors) {
            errors.forEach(error => dispatch(setAlert(error.msg, 'danger')));
        }

        dispatch({
            type: REGISTER_FAIL
        });
    }

};

// Login user
export const login = ({data}) => async dispatch => {

    try {
        setAuthToken(data.token)
        // Do not clearAuthorization before apply — avoids a window where
        // EnhancedDashboard loadUser can race and land on empty caps.
        if (data.authorization) {
            applyAuthorizationPayload(data);
        } else {
            clearAuthorization();
        }
        localStorage.removeItem('supportsNewPermission');

        dispatch({
            type: LOGIN_SUCCESS,
            payload: data
        });

        await dispatch(loadUser());
    } catch (err) {
        console.log("ERROR:", err);
        const errors = err;

        if (errors && Array.isArray(errors)) {
            errors.forEach(error => dispatch(setAlert(error.msg, 'danger')));
        }

        dispatch({
            type: LOGIN_FAIL
        });
    }

};

// Update user
export const updateUser = (data) => async dispatch => {
    dispatch({ type: AUTH_UPDATE });
    const config = {
        headers: {
            'Content-Type': 'application/json'
        }
    };
    const body = JSON.stringify(data);
    try {
        const res = await axios.patch('/api/auth', body, config);
        dispatch({
            type: USER_UPDATED,
            payload: data
        });
    } catch (err) {
        dispatch({
            type: AUTH_UPDATE_FAIL,
            payload: err.message
        });
    }
}

//Logout / Clear Profile
export const logout = () => dispatch => {
    delete axios.defaults.headers.common['x-auth-token'];
    clearAuthorization();
    dispatch({ type: CLEAR_PROFILE });
    dispatch({ type: LOGOUT });
};

export const signupSupport = async (body) => {
    try {
        body.phoneNumber = '+1' + body.phoneNumber;
        const { success, data } = await requestPost({ uri: '/api/onBoarding', body });
        return { data, success };
    } catch (e) {
        console.log(e.message);
    }
}
